import { useMemo } from 'react';
import type { FlowPlaysRow } from '@shared/flow/plays-view';
import { NOT_RESPONDED_TOOLTIP, PLAYS_FILTER_FIELDS, playTypeOptionsOf, withPlayTypeOptions } from '@shared/flow/plays-view';
import { PLAYS_TABLE_COLUMNS } from '@shared/flow/plays-table';
import type { FlowColumnDef, FlowTableConfig } from '../types';
import { EmDash, fmtDateOnly } from '../cells';
import { playsGroupValueOf } from './playsTableRows';
import { PlayRowActionsMenu } from './PlayRowActionsMenu';

/**
 * The Plays list's `FlowTable` config (plan
 * `plans/2026-09-11-004-plays-tab-qa-round2-dispatch.md` U17, amendment
 * **A11**). Replaces `PlaysAccordion`'s bespoke `COLUMN_RENDERERS` /
 * `SortHeader` / grouped-tbody table.
 *
 * A11's whole point: row height, header, zebra, sticky action column,
 * horizontal scroll, group collapse and every keyboard interaction become
 * identical to the People section BY CONSTRUCTION, because it is the same
 * component. What is left here is only the three things that are genuinely
 * about plays — how each column's cell reads, what a group means
 * (`groupValue`), and which row is the SELECTED play (`onRowClick` selects,
 * `rowClassName` highlights — plan 2026-09-24-005 U15/D6).
 *
 * Column KEYS are `PLAYS_TABLE_COLUMNS`', not `PLAYS_COLUMNS`' — see
 * `src/shared/flow/plays-table.ts` for why the Type column's key had to be
 * corrected from `play_type_name` to `play_type`.
 */

/**
 * Long free text (Subject/Body, default-visible): one line, with the FULL value on hover. The V1 `<td>` (`whitespace-nowrap max-w-[280px] truncate`)
 * and Glaze's V2 `TableCell` (`truncate`) cap width; `block truncate` here
 * makes the span itself nowrap + ellipsis so a multi-line plain-text body
 * (newlines are collapsed by nowrap) can never grow the row. The title attr
 * carries the full value; View Details shows it read-only.
 */
function TruncatedCell({ value }: { value: string | null }) {
  if (value == null) return <EmDash />;
  return <span className="block truncate" title={value}>{value}</span>;
}

/** `PLAYS_TABLE_COLUMNS` by key, so a label can never be typed twice. */
const LABELS: Record<string, string> = Object.fromEntries(PLAYS_TABLE_COLUMNS.map(c => [c.key, c.label]));

function label(key: string): string {
  return LABELS[key] ?? key;
}

/**
 * `FlowColumnDef`s for every `PLAYS_TABLE_COLUMNS` key — pinned key-for-key
 * by this unit's test, so a column added to the shared catalog cannot ship
 * without a renderer (and vice versa).
 *
 * The flag choices, all of which are about what the table OFFERS rather than
 * what it shows:
 *  - `noSort` on `play_type`/`status`/`city`/`target_persona`/the invite copy:
 *    `PLAYS_SORTABLE_KEYS` is the list `orderPlaysForTable` has comparators
 *    for, and a header that sorts on nothing is worse than a header that
 *    doesn't offer to.
 *  - `noGroup` on `name` and the invite copy: their values are ~unique per
 *    row, so grouping yields one single-row section per row. `target_persona`
 *    is excluded too — A7 fixed the group options at Type / Status / City and
 *    persona is free text on the campaign.
 *  - `start_date` (date), `capacity`, and the three counts (number) need no flag:
 *    `FlowTable` already excludes date/number columns from the Group picker.
 */
export const PLAYS_COLUMN_DEFS: FlowColumnDef<FlowPlaysRow>[] = [
  {
    key: 'name',
    label: label('name'),
    type: 'text',
    noGroup: true,
    render: row => (
      <>
        <div className="font-medium text-content-primary dark:text-nightshade-100" title={row.play_id}>{row.name}</div>
        {row.meta ? <div className="text-[11px] text-content-tertiary dark:text-nightshade-500">{row.meta}</div> : null}
      </>
    ),
  },
  {
    key: 'play_type',
    label: label('play_type'),
    type: 'picklist',
    noSort: true,
    // The cell shows the play type's NAME while the column's value (and so
    // its filter/group value) is the slug — the A2 readability rule applied
    // to a cell: the rep reads "Event Invite", the predicate runs on
    // `event_invite`, and `playTypeOptionsOf` is what maps between them.
    render: row => (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-bg-secondary dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400">
        {row.play_type_name}
      </span>
    ),
  },
  { key: 'status', label: label('status'), type: 'text', noSort: true, render: row => row.status ?? <EmDash /> },
  // launcher_sfdc_campaigns.start_date is a Postgres DATE sent as YYYY-MM-DD; fmtDate would read it as UTC midnight (prior day for US viewers).
  { key: 'start_date', label: label('start_date'), type: 'date', render: row => fmtDateOnly(row.start_date) },
  { key: 'city', label: label('city'), type: 'text', noSort: true, render: row => row.city ?? <EmDash /> },
  { key: 'target_persona', label: label('target_persona'), type: 'text', noSort: true, noGroup: true, render: row => row.target_persona ?? <EmDash /> },
  // The counts read out of `row.counts`, whose sub-keys the stored column
  // array flattens (see `PLAYS_COLUMNS`' doc) — this is the one place that
  // knows where each count key's value actually lives on the row.
  // `suggested`/`responded` are null for non-event plays and render a dash,
  // never a coerced 0 (D9).
  { key: 'capacity', label: label('capacity'), type: 'number', numeric: true, render: row => row.capacity ?? <EmDash /> },
  { key: 'suggested', label: label('suggested'), type: 'number', numeric: true, render: row => row.counts.suggested ?? <EmDash /> },
  // "Not Responded" (2026-09-22 plan) — label only; `headerTitle` carries the
  // explanation of what the bucket covers (the existing FlowColumnDef
  // pattern `ContactFlowTable.tsx`'s `signal_status` column already uses).
  { key: 'invited_sent', label: label('invited_sent'), type: 'number', numeric: true, headerTitle: NOT_RESPONDED_TOOLTIP, render: row => row.counts.invited_sent },
  { key: 'responded', label: label('responded'), type: 'number', numeric: true, render: row => row.counts.responded ?? <EmDash /> },
  { key: 'invite_subject', label: label('invite_subject'), type: 'text', noSort: true, noGroup: true, render: row => <TruncatedCell value={row.invite_subject} /> },
  { key: 'invite_body', label: label('invite_body'), type: 'text', noSort: true, noGroup: true, render: row => <TruncatedCell value={row.invite_body} /> },
];

/** The selected-row highlight (D6, reusing A4/D12's class). `bg-bg-blue-light` is the OPAQUE, theme-aware Clay token (`--color-bg-blue-light`, dark value in index.css). Opaque on purpose: `rowClassName` also replaces the sticky action cell's background (see `FlowTableConfig.rowClassName`). */
const SELECTED_ROW_CLS = 'bg-bg-blue-light';

export interface UsePlaysTableConfigArgs {
  /** The loaded plays — the source of `play_type`'s labeled filter options. */
  plays: FlowPlaysRow[];
  /** `?play=` — the selected play, highlighted; `null` when none is selected. */
  selectedPlayId: string | null;
  /** Row click (D6): select this play. Selection, not filtering — nothing on the People side is re-filtered. */
  onSelectPlay: (playId: string) => void;
  /** Which row's "Actions ▾" menu is open — one at a time, owned by the parent. */
  openMenuPlayId: string | null;
  setOpenMenuPlayId: (id: string | null) => void;
  /** U8 — threaded straight through to `PlayRowActionsMenu`; see that component's prop docs. */
  canManageEvents: boolean;
  onOpenDetails: (play: FlowPlaysRow, mode: 'view' | 'edit') => void;
}

export function usePlaysTableConfig({
  plays, selectedPlayId, onSelectPlay, openMenuPlayId, setOpenMenuPlayId, canManageEvents, onOpenDetails,
}: UsePlaysTableConfigArgs): FlowTableConfig<FlowPlaysRow> {
  const playTypeOptions = useMemo(() => playTypeOptionsOf(plays), [plays]);
  const filterFields = useMemo(() => withPlayTypeOptions(PLAYS_FILTER_FIELDS, playTypeOptions), [playTypeOptions]);

  return useMemo<FlowTableConfig<FlowPlaysRow>>(() => ({
    columns: PLAYS_COLUMN_DEFS,
    filterFields,
    rowKey: row => row.play_id,
    // D6 — a row click SELECTS the play (`?play=`). Legal on this config:
    // there is no `renderDetail` (the two are mutually exclusive).
    onRowClick: row => onSelectPlay(row.play_id),
    // A trailing sticky cell, exactly where `ContactFlowTable` puts its
    // "Enroll ⌄". The trigger stops propagation, so opening the menu never
    // also selects the row.
    rowAction: row => (
      <PlayRowActionsMenu
        play={row}
        open={openMenuPlayId === row.play_id}
        onOpenChange={open => setOpenMenuPlayId(open ? row.play_id : null)}
        onFilterForPlay={() => onSelectPlay(row.play_id)}
        canManageEvents={canManageEvents}
        onOpenDetails={onOpenDetails}
      />
    ),
    groupValue: playsGroupValueOf,
    rowClassName: row => (row.play_id === selectedPlayId ? SELECTED_ROW_CLS : undefined),
  }), [filterFields, selectedPlayId, onSelectPlay, openMenuPlayId, setOpenMenuPlayId, canManageEvents, onOpenDetails]);
}
